import React, { useState, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import { Button } from 'react-bootstrap';
import DetailActions from 'src/stores/alt/actions/DetailActions';

const clickToClose = (explorer) => {
  DetailActions.close(explorer, true);
};

export const CloseBtn = ({ explorer }) => (
  <Button
    variant="danger"
    size="xxsm"
    onClick={() => clickToClose(explorer)}
  >
    <i className="fa fa-times" />
  </Button>
);

export default function ExplorerComponent({ nodes, edges }) {
  const [rfNodes, , onNodesChange] = useNodesState(nodes);
  const [rfEdges, , onEdgesChange] = useEdgesState(edges);

  const [searchTerm, setSearchTerm] = useState('');
  const [focusSearchOnly, setFocusSearchOnly] = useState(true);

  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const matchesQuery = (value, query) => {
    const v = (value || '').toLowerCase();
    const q = (query || '').trim().toLowerCase();
    if (!q) return false;

    if (v === q) return true;

    const looksLikeLabel = /[\d._\-/]/.test(q);

    if (looksLikeLabel) {
      const boundaryPat = new RegExp(`(^|[\\s._\\-/])${escapeRegExp(q)}($|[\\s._\\-/])`, 'i');
      return boundaryPat.test(v);
    }

    return v.includes(q);
  };

  const nodeMap = useMemo(() => {
    const map = {};
    rfNodes.forEach((n) => {
      map[n.id] = n;
    });
    return map;
  }, [rfNodes]);

  // Edges whose endpoints don't (or no longer) resolve to a node are dropped defensively;
  // in practice every edge produced by positionNodesByReaction has both endpoints present.
  const validEdges = useMemo(
    () => rfEdges.filter((e) => nodeMap[e.source] && nodeMap[e.target]),
    [rfEdges, nodeMap]
  );

  const reactionSearchResult = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) {
      return {
        hasSearch: false,
        matchedReactionIds: new Set(),
        highlightedNodeIds: new Set(),
      };
    }

    const reactionNodes = rfNodes.filter((n) => n.type === 'reaction');
    const sampleNodes = rfNodes.filter((n) => n.type === 'sample');

    const matchedReactionIds = new Set(
      reactionNodes
        .filter((n) => {
          const shortLabel = (n.data?.reactionShortLabel || '').toLowerCase();
          const name = (n.data?.reactionName || '').toLowerCase();
          const label = (n.data?.label || '').toLowerCase();

          return (
            matchesQuery(shortLabel, q)
            || matchesQuery(name, q)
            || matchesQuery(label, q)
          );
        })
        .map((n) => n.id)
    );

    const matchedSampleIds = new Set(
      sampleNodes
        .filter((n) => {
          const label = (n.data?.label || '').toLowerCase();
          const sampleName = (n.data?.sampleName || '').toLowerCase();
          const sampleShortLabel = (n.data?.sampleShortLabel || '').toLowerCase();
          const sampleSmiles = (n.data?.sampleSmiles || '').toLowerCase();

          return (
            matchesQuery(label, q)
            || matchesQuery(sampleName, q)
            || matchesQuery(sampleShortLabel, q)
            || matchesQuery(sampleSmiles, q)
          );
        })
        .map((n) => n.id)
    );

    const reactionToSamples = {};
    const sampleToReactions = {};
    const splitParentReactionByChildReaction = {};
    const childReactionsByParentReaction = {};
    const reactionNodeById = {};

    reactionNodes.forEach((n) => {
      reactionNodeById[n.id] = n;
    });

    validEdges.forEach((e) => {
      const srcIsSample = e.source.startsWith('sample-');
      const srcIsReaction = e.source.startsWith('reaction-');
      const tgtIsSample = e.target.startsWith('sample-');
      const tgtIsReaction = e.target.startsWith('reaction-');

      if (srcIsSample && tgtIsReaction) {
        if (!reactionToSamples[e.target]) reactionToSamples[e.target] = new Set();
        reactionToSamples[e.target].add(e.source);

        if (!sampleToReactions[e.source]) sampleToReactions[e.source] = new Set();
        sampleToReactions[e.source].add(e.target);
      }

      if (srcIsReaction && tgtIsSample) {
        if (!reactionToSamples[e.source]) reactionToSamples[e.source] = new Set();
        reactionToSamples[e.source].add(e.target);

        if (!sampleToReactions[e.target]) sampleToReactions[e.target] = new Set();
        sampleToReactions[e.target].add(e.source);
      }
    });

    // Build parent/child reaction relation using split sample edges only one level.
    validEdges.forEach((e) => {
      const srcIsSample = e.source.startsWith('sample-');
      const tgtIsSample = e.target.startsWith('sample-');
      if (!(srcIsSample && tgtIsSample)) return;

      const parentSampleNodeId = e.source;
      const childSampleNodeId = e.target;

      const parentReactionIds = sampleToReactions[parentSampleNodeId] || new Set();
      const childReactionIds = sampleToReactions[childSampleNodeId] || new Set();

      childReactionIds.forEach((childRid) => {
        parentReactionIds.forEach((parentRid) => {
          splitParentReactionByChildReaction[childRid] = parentRid;
          if (!childReactionsByParentReaction[parentRid]) {
            childReactionsByParentReaction[parentRid] = new Set();
          }
          childReactionsByParentReaction[parentRid].add(childRid);
        });
      });
    });

    Object.keys(reactionNodeById).forEach((rid) => {
      const reagentNodeIds = reactionNodeById[rid]?.data?.reactionReagentNodeIds || [];
      reagentNodeIds.forEach((sid) => {
        if (!sampleToReactions[sid]) sampleToReactions[sid] = new Set();
        sampleToReactions[sid].add(rid);
      });
    });

    const directlyMatchedReactionIds = new Set([...matchedReactionIds]);

    matchedSampleIds.forEach((sid) => {
      const ownerReactions = sampleToReactions[sid] || new Set();
      ownerReactions.forEach((rid) => directlyMatchedReactionIds.add(rid));
    });

    const expandedReactionIds = new Set();

    directlyMatchedReactionIds.forEach((rid) => {
      expandedReactionIds.add(rid);

      const parentRid = splitParentReactionByChildReaction[rid];
      if (parentRid) {
        expandedReactionIds.add(parentRid);
      }

      const childRids = childReactionsByParentReaction[rid] || new Set();
      childRids.forEach((childRid) => expandedReactionIds.add(childRid));
    });

    const highlightedNodeIds = new Set();

    matchedSampleIds.forEach((sid) => highlightedNodeIds.add(sid));

    expandedReactionIds.forEach((rid) => {
      highlightedNodeIds.add(rid);

      const blockSamples = reactionToSamples[rid] || new Set();
      blockSamples.forEach((sid) => highlightedNodeIds.add(sid));

      const reagentNodeIds = reactionNodeById[rid]?.data?.reactionReagentNodeIds || [];
      reagentNodeIds.forEach((sid) => highlightedNodeIds.add(sid));
    });

    return {
      hasSearch: true,
      matchedReactionIds: directlyMatchedReactionIds,
      highlightedNodeIds,
    };
  }, [searchTerm, rfNodes, validEdges]);

  const displayNodes = useMemo(() => {
    if (!reactionSearchResult.hasSearch) return rfNodes;
    const { matchedReactionIds, highlightedNodeIds } = reactionSearchResult;

    if (focusSearchOnly) {
      return rfNodes
        .filter((n) => highlightedNodeIds.has(n.id))
        .map((n) => {
          const isMatchedReaction = matchedReactionIds.has(n.id);

          if (isMatchedReaction) {
            return {
              ...n,
              style: { ...(n.style || {}), boxShadow: '0 0 0 3px #2563eb' },
            };
          }

          if (n.type === 'reaction') {
            return {
              ...n,
              style: { ...(n.style || {}), boxShadow: '0 0 0 2px #60a5fa' },
            };
          }

          return {
            ...n,
            style: { ...(n.style || {}), boxShadow: '0 0 0 2px #22c55e' },
          };
        });
    }

    return rfNodes.map((n) => {
      const isHighlighted = highlightedNodeIds.has(n.id);
      const isMatchedReaction = matchedReactionIds.has(n.id);

      if (!isHighlighted) {
        return { ...n, style: { ...(n.style || {}), opacity: 0.2 } };
      }

      if (isMatchedReaction) {
        return {
          ...n,
          style: { ...(n.style || {}), opacity: 1, boxShadow: '0 0 0 3px #2563eb' },
        };
      }

      if (n.type === 'reaction') {
        return {
          ...n,
          style: { ...(n.style || {}), opacity: 1, boxShadow: '0 0 0 2px #60a5fa' },
        };
      }

      return {
        ...n,
        style: { ...(n.style || {}), opacity: 1, boxShadow: '0 0 0 2px #22c55e' },
      };
    });
  }, [rfNodes, reactionSearchResult, focusSearchOnly]);

  const displayEdges = useMemo(() => {
    if (!reactionSearchResult.hasSearch) return validEdges;
    const { highlightedNodeIds } = reactionSearchResult;

    if (focusSearchOnly) {
      return validEdges
        .filter((e) => highlightedNodeIds.has(e.source) && highlightedNodeIds.has(e.target))
        .map((e) => ({
          ...e,
          style: {
            ...(e.style || {}),
            opacity: 1,
            strokeWidth: (e.style?.strokeWidth || 1.5) + 0.5,
          },
          labelStyle: {
            ...(e.labelStyle || {}),
            opacity: 1,
            fontWeight: 600,
          },
        }));
    }

    return validEdges.map((e) => {
      const isHighlighted =
        highlightedNodeIds.has(e.source) && highlightedNodeIds.has(e.target);

      if (!isHighlighted) {
        return {
          ...e,
          style: { ...(e.style || {}), opacity: 0.12 },
          labelStyle: { ...(e.labelStyle || {}), opacity: 0.12 },
        };
      }

      return {
        ...e,
        style: {
          ...(e.style || {}),
          opacity: 1,
          strokeWidth: (e.style?.strokeWidth || 1.5) + 0.5,
        },
        labelStyle: {
          ...(e.labelStyle || {}),
          opacity: 1,
          fontWeight: 600,
        },
      };
    });
  }, [validEdges, reactionSearchResult, focusSearchOnly]);

  const wrapperRef = useRef(null);
  const [hover, setHover] = useState(null);

  const getHoverBoxSize = (kind) => {
    const reactionWidth = Math.min(900, window.innerWidth * 0.75);
    return kind === 'reaction'
      ? { w: reactionWidth, h: Math.min(window.innerHeight * 0.65 + 80, 560) }
      : { w: 220, h: 260 };
  };

  const clampHoverPos = (rawX, rawY, kind) => {
    if (!wrapperRef.current) return { x: rawX, y: rawY };
    const rect = wrapperRef.current.getBoundingClientRect();
    const { w, h } = getHoverBoxSize(kind);

    const maxX = Math.max(8, rect.width - w - 8);
    const maxY = Math.max(8, rect.height - h - 8);

    return {
      x: Math.min(Math.max(8, rawX), maxX),
      y: Math.min(Math.max(8, rawY), maxY),
    };
  };

  const updateHoverPos = (event) => {
    if (!hover || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const rawX = event.clientX - rect.left + 12;
    const rawY = event.clientY - rect.top + 12;
    const pos = clampHoverPos(rawX, rawY, hover.kind);

    setHover((prev) => prev && ({ ...prev, x: pos.x, y: pos.y }));
  };

  const formatPerformedAt = (isoDate) => {
    if (!isoDate) return 'Date not available';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return 'Date not available';
    return date.toLocaleString();
  };

  return (
    <div
      ref={wrapperRef}
      style={{ height: '80vh', width: '100%', position: 'relative' }}
      onMouseMove={updateHoverPos}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 60,
          background: 'white',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          padding: 8,
          width: 320,
        }}
      >
        <input
          type="text"
          placeholder="Search reaction or sample by label/name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '6px 8px',
            fontSize: 12,
          }}
        />
        <label style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
          <input
            type="checkbox"
            checked={focusSearchOnly}
            onChange={(e) => setFocusSearchOnly(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Show only search result
        </label>
      </div>

      {hover && (hover.src || hover.text) && (
        <div
          style={{
            position: 'absolute',
            left: hover.x,
            top: hover.y,
            background: 'white',
            border: '1px solid #d1d5db',
            padding: 10,
            borderRadius: 6,
            zIndex: 50,
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
            width: hover.kind === 'reaction' ? Math.min(900, window.innerWidth * 0.75) : 220,
            pointerEvents: 'none',
          }}
        >
          {(hover.text || hover.meta) && (
            <div style={{ marginBottom: hover.src ? 8 : 0 }}>
              <div style={{ fontSize: 12 }}>{hover.text}</div>
              {hover.meta && (
                <div style={{ fontSize: 11, textAlign: 'right', color: '#6b7280' }}>
                  {hover.meta}
                </div>
              )}
            </div>
          )}

          {hover.src && (
            <img
              src={hover.src}
              alt={hover.text || 'Preview'}
              style={
                hover.kind === 'reaction'
                  ? {
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: '65vh',
                    objectFit: 'contain',
                  }
                  : {
                    width: 220,
                    height: 220,
                    objectFit: 'contain',
                  }
              }
            />
          )}
        </div>
      )}

      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        onNodeMouseEnter={(event, node) => {
          if (!wrapperRef.current) return;
          const rect = wrapperRef.current.getBoundingClientRect();

          const isSample = node?.type === 'sample';
          const isReaction = node?.type === 'reaction';

          let src = null;
          let text = null;
          let meta = null;
          let kind = null;

          if (isSample) {
            src = node?.data?.image;
            text = node?.data?.label;
            kind = 'sample';
          }

          if (isReaction) {
            src = node?.data?.reactionImage;
            const shortLabel = node?.data?.reactionShortLabel || '';
            const name = node?.data?.reactionName || '';
            const performedAt = node?.data?.reactionPerformedAt;
            const performedText = formatPerformedAt(performedAt);

            text = shortLabel && name ? `${shortLabel}: ${name}` : (shortLabel || name || 'Reaction');
            meta = `Performed: ${performedText}`;
            kind = 'reaction';
          }

          if (!src && !text) return;

          const rawX = event.clientX - rect.left + 12;
          const rawY = event.clientY - rect.top + 12;
          const pos = clampHoverPos(rawX, rawY, kind);

          setHover({
            src,
            text,
            meta,
            kind,
            x: pos.x,
            y: pos.y,
          });
        }}
        onNodeMouseLeave={() => setHover(null)}
      >
        <MiniMap />
        <Controls />
        <Background gap={16} />
      </ReactFlow>
    </div>
  );
}
